import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, TrendingUp, Clock, Car, ArrowUpRight, Plus, AlertCircle, Bookmark, Coins, ArrowDownLeft, ArrowUpRight as ArrowUp, Hourglass } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { api } from '../lib/api';
import { formatBRL, formatPercent, VEHICLE_STATUS } from '../lib/format';
import KpiCard from '../components/KpiCard';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import VehicleStatusBadge from '../components/VehicleStatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const STATUS_COLORS = {
  PURCHASED: '#64748B',
  IN_PREPARATION: '#2563EB',
  AVAILABLE: '#10B981',
  SOLD: '#1F7A5A',
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard')
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  const k = data?.kpis || {};
  const hasAnyData = (k.sold_count || 0) + (k.active_count || 0) > 0;

  const statusPieData = Object.entries(data?.status_counts || {}).map(([key, value]) => ({
    name: VEHICLE_STATUS[key]?.label || key,
    value,
    key,
  }));

  return (
    <>
      <PageHeader
        title="Visão geral"
        subtitle="Acompanhe capital investido, lucro acumulado e status da operação em tempo real."
        actions={
          <Link to="/veiculos/novo">
            <Button className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="dashboard-new-vehicle-button">
              <Plus className="h-4 w-4 mr-1.5" /> Novo veículo
            </Button>
          </Link>
        }
      />

      {!hasAnyData ? (
        <EmptyState
          icon={Car}
          title="Comece cadastrando seu primeiro veículo"
          description="Cadastre sócios, registre a compra e despesas, e veja a distribuição de resultado automaticamente."
          action={{ label: 'Adicionar veículo', onClick: () => window.location.href = '/veiculos/novo' }}
        />
      ) : (
        <>
          {k.stub_count > 0 && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center gap-3" data-testid="dashboard-stub-alert">
              <AlertCircle className="h-5 w-5 text-amber-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-amber-900">Você tem {k.stub_count} veículo{k.stub_count > 1 ? 's' : ''} com cadastro pendente</div>
                {data.stub_vehicles?.length > 0 && (
                  <div className="text-xs text-amber-800 mt-0.5 truncate">
                    {data.stub_vehicles.map((s, i) => (
                      <span key={s.id}>
                        {i > 0 && ' · '}
                        <Link to={`/veiculos/${s.id}`} className="hover:underline">{s.label} ({s.plate})</Link>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/veiculos">
                <Button size="sm" variant="outline" className="bg-white">Ver todos</Button>
              </Link>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard
              label="Em estoque"
              value={formatBRL(k.total_capital_in_stock)}
              icon={Car}
              sublabel={`${k.active_count} veículo(s) ativos${k.reserved_count > 0 ? ` · ${k.reserved_count} reservado(s)` : ''}`}
              testid="kpi-capital-stock"
            />
            <KpiCard
              label="Em caixa"
              value={formatBRL(k.total_cash || k.total_partner_balance || 0)}
              icon={Wallet}
              intent={(k.total_cash || 0) >= 0 ? 'success' : 'danger'}
              sublabel={
                (k.total_reservation_deposits || 0) > 0
                  ? `${formatBRL(k.total_partner_balance || 0)} sócios + ${formatBRL(k.total_reservation_deposits)} em sinais`
                  : 'realizado · data ≤ hoje'
              }
              testid="kpi-cash"
            />
            <KpiCard
              label="A receber"
              value={formatBRL(k.total_to_receive || 0)}
              icon={Coins}
              intent={(k.total_to_receive || 0) > 0 ? 'success' : 'default'}
              sublabel="vendas futuras · data > hoje"
              testid="kpi-to-receive-main"
            />
            <KpiCard
              label="Lucro acumulado"
              value={formatBRL(k.total_profit_accumulated)}
              icon={TrendingUp}
              intent={k.total_profit_accumulated >= 0 ? 'success' : 'danger'}
              sublabel={`${k.sold_count} venda(s) · ROI ${formatPercent(k.avg_roi)}`}
              testid="kpi-profit-accumulated"
            />
          </div>

          {/* Fluxo de caixa — Realizado vs Previsto (DETALHADO) */}
          {((k.total_to_receive || 0) > 0 || (k.total_to_pay || 0) > 0) && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <Hourglass className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">Detalhamento do fluxo previsto</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KpiCard
                  label="A receber"
                  value={formatBRL(k.total_to_receive || 0)}
                  icon={ArrowDownLeft}
                  intent={(k.total_to_receive || 0) > 0 ? 'success' : 'default'}
                  sublabel="Vendas e créditos futuros"
                  testid="kpi-to-receive"
                />
                <KpiCard
                  label="A pagar"
                  value={formatBRL(k.total_to_pay || 0)}
                  icon={ArrowUp}
                  intent={(k.total_to_pay || 0) > 0 ? 'danger' : 'default'}
                  sublabel={
                    (k.future_expenses || 0) > 0
                      ? `Despesas: ${formatBRL(k.future_expenses)} · Saques: ${formatBRL((k.total_to_pay || 0) - (k.future_expenses || 0))}`
                      : 'Saques agendados e débitos futuros'
                  }
                  testid="kpi-to-pay"
                />
                <KpiCard
                  label="Saldo projetado"
                  value={formatBRL(k.projected_balance || 0)}
                  icon={TrendingUp}
                  intent={(k.projected_balance || 0) >= 0 ? 'success' : 'danger'}
                  sublabel={`Caixa ${formatBRL(k.total_cash || k.total_partner_balance || 0)} + previstos`}
                  testid="kpi-projected-balance"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
            <Card className="lg:col-span-2 border-border/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Lucro por mês</CardTitle>
              </CardHeader>
              <CardContent>
                {data.profit_by_month?.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={data.profit_by_month} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                      <YAxis stroke="#6B7280" fontSize={12} tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value) => formatBRL(value)}
                        labelFormatter={(l) => `Mês: ${l}`}
                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                      />
                      <Line type="monotone" dataKey="profit" stroke="#0B1F3B" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="py-12 text-center text-sm text-muted-foreground">Sem vendas registradas ainda.</div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Distribuição por status</CardTitle>
              </CardHeader>
              <CardContent>
                {statusPieData.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={statusPieData} dataKey="value" cx="50%" cy="45%" innerRadius={45} outerRadius={75}>
                        {statusPieData.map((entry) => (
                          <Cell key={entry.key} fill={STATUS_COLORS[entry.key] || '#94A3B8'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value} veíc.`} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                      <Legend verticalAlign="bottom" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="py-12 text-center text-sm text-muted-foreground">Sem dados.</div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            <Card className="border-border/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Top veículos por lucro</CardTitle>
              </CardHeader>
              <CardContent>
                {data.top_vehicles?.length ? (
                  <div className="divide-y divide-border/70">
                    {data.top_vehicles.map((v) => (
                      <Link to={`/veiculos/${v.id}`} key={v.id} className="flex items-center justify-between py-3 hover:bg-muted/40 -mx-2 px-2 rounded-lg transition-colors" data-testid="top-vehicle-row">
                        <div className="text-sm font-medium">{v.label}</div>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-muted-foreground tabular">{formatPercent(v.roi)}</span>
                          <span className={`text-sm font-semibold tabular ${v.net_result >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>
                            {formatBRL(v.net_result, { sign: true })}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-muted-foreground">Sem vendas ainda.</div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Veículos recentes</CardTitle>
              </CardHeader>
              <CardContent>
                {data.recent_vehicles?.length ? (
                  <div className="divide-y divide-border/70">
                    {data.recent_vehicles.map((v) => (
                      <Link to={`/veiculos/${v.id}`} key={v.id} className="flex items-center justify-between py-3 hover:bg-muted/40 -mx-2 px-2 rounded-lg transition-colors">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{v.brand} {v.model} {v.year}</div>
                          <div className="text-xs text-muted-foreground tabular">{formatBRL(v.purchase_price)}</div>
                        </div>
                        <VehicleStatusBadge status={v.status} />
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-muted-foreground">Sem veículos cadastrados.</div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
